/**
 * Accent/case/typo-insensitive text search helpers.
 *
 * normalizeText: lowercases, strips diacritics (á→a, ñ→n, ü→u), collapses
 *   whitespace and trims. Used to compare query vs target ignoring accents,
 *   case and spacing.
 *
 * fuzzyMatch: returns true if the (normalized) query matches the (normalized)
 *   target tolerating minor spelling mistakes. Strategy:
 *     1. direct substring match (fast path), then
 *     2. token-by-token: every query token must match some target token either
 *        as a substring or within a small Levenshtein edit distance (1 for
 *        short tokens, 2 for longer ones) — this absorbs typos like
 *        "interpres" → "interpretes" or "mexco" → "mexico".
 */

export function normalizeText(s) {
  return String(s == null ? '' : s)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip combining diacritical marks
    .replace(/[._\-/|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

// Classic Levenshtein edit distance (early-exit when over `max`).
function editDistance(a, b, max) {
  const al = a.length;
  const bl = b.length;
  if (Math.abs(al - bl) > max) return max + 1;
  if (!al) return bl;
  if (!bl) return al;
  let prev = new Array(bl + 1);
  let curr = new Array(bl + 1);
  for (let j = 0; j <= bl; j += 1) prev[j] = j;
  for (let i = 1; i <= al; i += 1) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j <= bl; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) return max + 1; // whole row exceeded budget
    [prev, curr] = [curr, prev];
  }
  return prev[bl];
}

function tolFor(len) {
  if (len <= 3) return 0;
  if (len <= 5) return 1;
  return 2;
}

function tokenMatches(qTok, targetTokens) {
  const tol = tolFor(qTok.length);
  for (const tTok of targetTokens) {
    // Forward substring only: the target token must contain the query token
    // (prefix/substring search). The reverse direction (query contains target)
    // caused false positives — e.g. a 1-char target token like "a" is a
    // substring of the query "liang", matching unrelated records.
    if (tTok.includes(qTok)) return true;
    if (tol > 0 && editDistance(qTok, tTok, tol) <= tol) return true;
  }
  return false;
}

/**
 * @param {string} query raw search query
 * @param {string} target raw target text (will be normalized)
 * @returns {boolean}
 */
export function fuzzyMatch(query, target) {
  const q = normalizeText(query);
  if (!q) return true;
  const t = normalizeText(target);
  if (!t) return false;
  if (t.includes(q)) return true; // fast path: accent/case-insensitive substring
  const qTokens = q.split(' ').filter(Boolean);
  const tTokens = t.split(' ').filter(Boolean);
  if (!tTokens.length) return false;
  // Every query token must fuzzily match some target token.
  return qTokens.every((qt) => tokenMatches(qt, tTokens));
}
